'use client';

import { usePathname, useRouter } from 'next/navigation';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { navigableAccountItems } from '@/features/settings/model/account-menu';

/**
 * Prev/next chevrons for moving between sibling Account settings pages.
 * Mirrors Apple's `accountSettingsNavToolbar` (chevron.backward/.forward in
 * topBarTrailing) and the Android `AccountSettingsNavActions` TopAppBar slot.
 *
 * Renders nothing when the current path isn't part of the pager (root,
 * privacy/terms/delete, unknown sub-routes).
 */
export function AccountSubpageNav() {
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations('account');

  const items = navigableAccountItems;
  const currentIndex = items.findIndex(
    (item) => pathname === item.href || pathname.startsWith(item.href + '/')
  );
  if (currentIndex === -1) return null;

  const prev = items[currentIndex - 1];
  const next = items[currentIndex + 1];
  const baseClass =
    'flex items-center justify-center h-8 w-8 rounded-full text-primary transition-opacity disabled:opacity-30 disabled:cursor-default hover:bg-muted';

  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        className={baseClass}
        onClick={() => prev && router.replace(prev.href)}
        disabled={!prev}
        aria-label={t('previousSetting')}
      >
        <ChevronLeft className="h-5 w-5 rtl:-scale-x-100" />
      </button>
      <button
        type="button"
        className={baseClass}
        onClick={() => next && router.replace(next.href)}
        disabled={!next}
        aria-label={t('nextSetting')}
      >
        <ChevronRight className="h-5 w-5 rtl:-scale-x-100" />
      </button>
    </div>
  );
}
